/**
 * Password Recovery Form Component
 *
 * A form for requesting a password reset email.
 * Users enter their email and receive a reset link.
 *
 * @fileoverview Password recovery form with Supabase authentication
 *
 * @example
 * <PasswordRecoveryForm />
 */

import React, { useState, FormEvent, useCallback } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Text,
  Link as ChakraLink,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { MdEmail } from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail } from '../../utils/validation';
import AuthLayout from './AuthLayout';

/**
 * Password Recovery Form Component
 *
 * Handles sending password reset emails.
 * Features:
 * - Email validation
 * - Success message with instructions
 * - Error handling
 * - Back to login link
 *
 * @returns Password recovery form component
 *
 * @example
 * function ForgotPasswordPage() {
 *   return <PasswordRecoveryForm />;
 * }
 */
export const PasswordRecoveryForm: React.FC = () => {
  const { resetPassword, loading, error, clearError } = useAuth();
  const toast = useToast();

  // Form state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Color mode values
  const linkColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');

  /**
   * Handle email input blur for validation
   */
  const handleEmailBlur = useCallback(() => {
    setTouched(true);
    setEmailError(validateEmail(email));
  }, [email]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      clearError();

      setTouched(true);
      const validation = validateEmail(email);
      setEmailError(validation);

      if (validation) {
        return;
      }

      const result = await resetPassword(email);

      if (result.success) {
        toast({
          title: 'Email sent',
          description: 'The password reset email has been sent successfully.',
          status: 'success',
          duration: 6000,
          isClosable: true,
          position: 'top',
        });
        setSubmitted(true);
      }
    },
    [email, resetPassword, clearError]
  );

  // Show success message after submission
  if (submitted) {
    return (
      <AuthLayout
        title="Check Your Email"
        subtitle="We've sent you password reset instructions"
      >
        <VStack spacing={6}>
          <Icon as={MdEmail} boxSize={16} color={linkColor} />
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              The password reset email has been sent successfully.
            </AlertDescription>
          </Alert>
          <Text fontSize="sm" textAlign="center" color="gray.500">
            Check your inbox at <strong>{email}</strong> and your spam folder.
            The link will expire in 24 hours.
          </Text>
          <ChakraLink
            as={RouterLink}
            to="/"
            color={linkColor}
            fontWeight="medium"
            display="flex"
            alignItems="center"
          >
            <ArrowBackIcon mr={2} />
            Return to Sign In
          </ChakraLink>
        </VStack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot Password?"
      subtitle="Enter your email to receive a reset link"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={5}>
          {/* Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email Field */}
          <FormControl isInvalid={touched && !!emailError}>
            <FormLabel>Email Address</FormLabel>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              autoComplete="email"
              size="lg"
            />
            <FormErrorMessage>{emailError}</FormErrorMessage>
          </FormControl>

          {/* Submit Button */}
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            w="full"
            isLoading={loading}
            loadingText="Sending..."
          >
            Send Reset Link
          </Button>

          {/* Back to Login Link */}
          <ChakraLink
            as={RouterLink}
            to="/"
            color={linkColor}
            fontWeight="medium"
            fontSize="sm"
            display="flex"
            alignItems="center"
          >
            <ArrowBackIcon mr={2} />
            Back to Sign In
          </ChakraLink>
        </VStack>
      </form>
    </AuthLayout>
  );
};

export default PasswordRecoveryForm;
